import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { AppProvider } from './contexts/AppContext.tsx';
import MainLayout from './components/Layout/MainLayout.tsx';
import LoginForm from './components/Auth/LoginForm.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Users from './pages/Users.tsx';
import Profile from './pages/Profile.tsx';
import Customers from './pages/Customers.tsx';
import Couriers from './pages/Couriers.tsx';
import Suppliers from './pages/Suppliers.tsx';
import Importers from './pages/Importers.tsx';
import Warehouses from './pages/Warehouses.tsx';
import Products from './pages/Products.tsx';
import CustomerOrdersNew from './pages/CustomerOrdersNew.tsx';
import SupplierOrders from './pages/SupplierOrders.tsx';
import ShippingDestinations from './pages/ShippingDestinations.tsx';
import CustomerQuotes from './pages/CustomerQuotes.tsx';
import SupplierQuotes from './pages/SupplierQuotes.tsx';
import Inventory from './pages/Inventory.tsx';
import DevTools from './pages/DevTools.tsx';
import MissingCustomerAssociations from './pages/MissingCustomerAssociations.tsx';
import StockWithoutCustomer from './pages/StockWithoutCustomer.tsx';
import RollbackEvasioneOrdine from './pages/RollbackEvasioneOrdine.tsx';
import BackupDatabase from './pages/BackupDatabase.tsx';
import LoginAttempts from './pages/LoginAttempts.tsx';
import FormazioneTmp from './pages/FormazioneTmp.tsx';
import Formazione from './pages/Formazione.tsx';
import AISettings from './pages/AISettings.tsx';
import Permissions from './pages/Permissions.tsx';
import ImportAliases from './pages/ImportAliases.tsx';
import ContainerTypes from './pages/ContainerTypes.tsx';
import InternationalShipmentStatuses from './pages/InternationalShipmentStatuses.tsx';
// Legacy import rimosso: CargoLoading
import CustomsDocuments from './pages/CustomsDocuments.tsx';
import UnifiedShipments from './pages/UnifiedShipments.tsx';
import ProductMovements from './pages/ProductMovements.tsx';
import ReportGiacenze from './pages/ReportGiacenze.tsx';
import ReportSituazioneCliente from './pages/ReportSituazioneCliente.tsx';
import ControlloQuantitaOron from './pages/ControlloQuantitaOron.tsx';
import ControlloUDC from './pages/ControlloUDC.tsx';
import ControlloFatturaCorriere from './pages/ControlloFatturaCorriere.tsx';
import 'antd/dist/reset.css';
import './utils/axiosConfig.ts'; // Initialize axios interceptors


const ProtectedRoute: React.FC<{ children: React.ReactNode; resource?: string }> = ({ children, resource }) => {
  const { user, loading, canRead } = useAuth();

  if (loading) {
    return <div>Caricamento...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Guardia permessi: senza lettura sulla risorsa la pagina non è raggiungibile
  if (resource && !canRead(resource)) {
    if (resource === 'dashboard') {
      // niente redirect loop: la dashboard è la landing page
      return (
        <MainLayout>
          <div style={{ padding: 24 }}>Nessun permesso di accesso a questa sezione.</div>
        </MainLayout>
      );
    }
    return <Navigate to="/dashboard" />;
  }

  return <MainLayout>{children}</MainLayout>;
};

const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Caricamento...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.level_id !== 1) {
    return <Navigate to="/dashboard" />;
  }

  return <MainLayout>{children}</MainLayout>;
};

function AppContent() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginForm />} />
            <Route
              path="/"
              element={
                <ProtectedRoute resource="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute resource="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminOnlyRoute>
                  <Users />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute resource="customers">
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/couriers"
              element={
                <ProtectedRoute resource="couriers">
                  <Couriers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute resource="suppliers">
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/importers"
              element={
                <ProtectedRoute resource="importers">
                  <Importers />
                </ProtectedRoute>
              }
            />            
            <Route
              path="/warehouses"
              element={
                <ProtectedRoute resource="warehouses">
                  <Warehouses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute resource="products">
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-orders"
              element={
                <ProtectedRoute resource="customer-orders">
                  <CustomerOrdersNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplier-orders"
              element={
                <ProtectedRoute resource="supplier-orders">
                  <SupplierOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipping-destinations"
              element={
                <ProtectedRoute resource="shipping-destinations">
                  <ShippingDestinations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-quotes"
              element={
                <ProtectedRoute resource="customer-quotes">
                  <CustomerQuotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplier-quotes"
              element={
                <ProtectedRoute resource="supplier-quotes">
                  <SupplierQuotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute resource="inventory">
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-movements"
              element={
                <ProtectedRoute resource="product-movements">
                  <ProductMovements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/formazione"
              element={
                <ProtectedRoute resource="formazione-auto">
                  <Formazione />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dev-tools"
              element={
                <AdminOnlyRoute>
                  <DevTools />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/tools/missing-customer-associations"
              element={
                <AdminOnlyRoute>
                  <MissingCustomerAssociations />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/tools/stock-without-customer"
              element={
                <AdminOnlyRoute>
                  <StockWithoutCustomer />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/tools/rollback-evasione-ordine"
              element={
                <AdminOnlyRoute>
                  <RollbackEvasioneOrdine />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/tools/backup-db"
              element={
                <AdminOnlyRoute>
                  <BackupDatabase />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/tools/login-attempts"
              element={
                <AdminOnlyRoute>
                  <LoginAttempts />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/formazione-tmp"
              element={
                <AdminOnlyRoute>
                  <FormazioneTmp />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/settings/api"
              element={
                <AdminOnlyRoute>
                  <AISettings />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/settings/permissions"
              element={
                <AdminOnlyRoute>
                  <Permissions />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/settings/aliases"
              element={
                <AdminOnlyRoute>
                  <ImportAliases />
                </AdminOnlyRoute>
              }
            />
            <Route
              path="/container-types"
              element={
                <ProtectedRoute resource="container-types">
                  <ContainerTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/international-shipment-statuses"
              element={
                <ProtectedRoute resource="international-shipment-statuses">
                  <InternationalShipmentStatuses />
                </ProtectedRoute>
              }
            />
            {/* Route /cargo-loading rimossa - funzionalità integrata in customs-documents */}
            <Route
              path="/customs-documents"
              element={
                <ProtectedRoute resource="customs-documents">
                  <CustomsDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/unified-shipments"
              element={
                <ProtectedRoute resource="unified-shipments">
                  <UnifiedShipments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report-giacenze"
              element={
                <ProtectedRoute resource="report-giacenze">
                  <ReportGiacenze />
                </ProtectedRoute>
              }
            />
            <Route
              path="/report-situazione-cliente"
              element={
                <ProtectedRoute resource="report-situazione-cliente">
                  <ReportSituazioneCliente />
                </ProtectedRoute>
              }
            />
            <Route
              path="/utilita/controllo-quantita-oron"
              element={
                <ProtectedRoute resource="utilita-quantita-oron">
                  <ControlloQuantitaOron />
                </ProtectedRoute>
              }
            />
            <Route
              path="/utilita/controllo-udc"
              element={
                <ProtectedRoute resource="utilita-udc">
                  <ControlloUDC />
                </ProtectedRoute>
              }
            />
            <Route
              path="/utilita/controllo-fattura-corriere"
              element={
                <ProtectedRoute resource="utilita-fattura-corriere">
                  <ControlloFatturaCorriere />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <ConfigProvider>
          <AppContent />
        </ConfigProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;